import { captureException } from "@sentry/nextjs";
import { GraphQLSingularResponse } from "relay-runtime";

import { DuetError } from "@framework/relay/errors";
import { log } from "@framework/relay/logger";
import {
  GraphQLResponse,
  GraphQLResponseWithErrors,
} from "@framework/relay/shared/GraphQLResponse";
import Middleware from "@framework/relay/shared/Middleware";

export function hasResponseErrors(
  response: GraphQLResponse,
): response is GraphQLResponseWithErrors {
  return !!response.errors;
}

/**
 * Treat all errors as critical for now, we can revisit this later.
 */
export function isErrorCritical(response: GraphQLResponseWithErrors) {
  if (!response.data) {
    return true;
  }
  return response.errors.length > 0;
}

const errorMiddleware: Middleware = (next) => async (ctx) => {
  // Cast it to the response type that we expect from the server.
  const response = (await next(ctx)) as GraphQLResponse;

  // If the response has errors, throw an error.
  if (hasResponseErrors(response)) {
    const context = {
      url: ctx.url,
      errors: response.errors,
      hasData: !!response.data,
      requestMethod: ctx.fetchOpts.method,
      incomingRequestContext: ctx.incomingRequestContext,
      ...ctx.logContext,
    };

    if (
      isErrorCritical(response) &&
      ctx.cacheConfig.metadata?.throwOnError !== false
    ) {
      // Log critical error with full context
      log.error(context, "GraphQL Response with critical errors");

      // If error is critical, we should throw an error.
      throw new DuetError("GraphQL Response with critical errors", {
        extra: context,
      });
    } else {
      // Log non-critical error with full context
      log.warn(context, "GraphQL Response with errors");

      // Capture error but don't throw.
      captureException(
        new DuetError("GraphQL Response with errors", {
          extra: context,
        }),
      );
    }
  }

  // Cast back to Relay type to avoid typescript error.
  return response as GraphQLSingularResponse;
};

export default errorMiddleware;
